import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  useGetMyLanguageQuery,
  useGetLanguageStringsQuery,
  useUpdateMyLanguageMutation,
} from "../redux/admin/userApi";

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const { data: myLang, isLoading: myLangLoading } = useGetMyLanguageQuery();

  const [lang, setLang] = useState("hi");

  // sync with backend saved lang
  useEffect(() => {
    if (myLang) setLang(myLang);
  }, [myLang]);

  const {
    data: strings = {},
    isLoading: stringsLoading,
    refetch: refetchStrings,
  } = useGetLanguageStringsQuery(lang, { skip: !lang });

  const [updateMyLanguage, { isLoading: saving }] = useUpdateMyLanguageMutation();

  const t = useMemo(() => {
    return (key, fallback) => {
      if (!key) return "";
      return strings?.[key] ?? fallback ?? key;
    };
  }, [strings]);

  const changeLanguage = async (newLang) => {
    setLang(newLang); // ✅ instant UI change attempt (optimistic)
    try {
      await updateMyLanguage(newLang).unwrap(); // ✅ save on backend
      refetchStrings(); // ✅ reload strings
      localStorage.setItem("app_lang", newLang);
    } catch (e) {
      // fallback if save fails: restore old
      setLang(myLang || "hi");
    }
  };

  const value = useMemo(
    () => ({
      lang,
      t,
      changeLanguage,
      loading: myLangLoading || stringsLoading,
      saving,
    }),
    [lang, t, changeLanguage, myLangLoading, stringsLoading, saving]
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside <LanguageProvider />");
  return ctx;
};
